/**
 * Frontend contract for the structured song domain.
 *
 * ChordAnchor.position is a Unicode code-point offset in line.text. JavaScript
 * strings use UTF-16 indices, so callers must use the helpers below at the UI
 * boundary and must never treat selectionStart as a domain position directly.
 */

export type StructuredSectionType =
  | 'verse' | 'chorus' | 'pre_chorus' | 'bridge' | 'intro' | 'outro'
  | 'interlude' | 'tag' | 'instrumental' | 'custom';

export type PreservedFragmentScope = 'song' | 'arrangement' | 'section' | 'line';
export type StructuredConversionStatus = 'clean' | 'needs_review' | 'unsupported';

export interface StructuredSongMetadata {
  title?: string;
  subtitle?: string;
  artist?: string;
  composer?: string;
  lyricist?: string;
  copyright?: string;
  ccli?: string;
  language?: string;
  tags?: string;
  publisher?: string;
  license?: string;
  source?: string;
  album?: string;
  year?: string;
  duration?: string;
  [key: string]: string | undefined;
}

export interface ChordAnchor {
  /** Unicode code-point offset, not a UTF-16 JavaScript index. */
  position: number;
  name: string;
}

export interface PreservedChordProFragment {
  scope: PreservedFragmentScope;
  raw: string;
  original_order: number;
  reason: string;
  section_stable_key?: string;
  line_stable_key?: string;
  line_order?: number;
  position?: number;
}

export interface StructuredLine {
  stable_key: string;
  order: number;
  text: string;
  chords: ChordAnchor[];
  preserved_fragments: PreservedChordProFragment[];
}

export interface StructuredSection {
  stable_key: string;
  type: StructuredSectionType;
  label: string;
  order: number;
  notes?: string;
  lines: StructuredLine[];
  preserved_fragments: PreservedChordProFragment[];
}

export interface StructuredArrangement {
  stable_key: string;
  name: string;
  default_key?: string;
  bpm?: string;
  meter?: string;
  capo?: string;
  notes?: string;
  sections: StructuredSection[];
  preserved_fragments: PreservedChordProFragment[];
}

export interface StructuredSong {
  metadata: StructuredSongMetadata;
  arrangements: StructuredArrangement[];
  preserved_fragments: PreservedChordProFragment[];
}

export interface StructuredConversionWarning {
  code: string;
  message: string;
  severity: 'warning' | 'error';
  source_order?: number;
}

export interface StructuredConversionResult {
  status: StructuredConversionStatus;
  song: StructuredSong;
  warnings: StructuredConversionWarning[];
  preserved_fragments: PreservedChordProFragment[];
}

/** Convert a JavaScript UTF-16 index into a Unicode code-point offset. */
export const utf16IndexToCodePointOffset = (value: string, utf16Index: number): number => (
  Array.from(value.slice(0, Math.max(0, utf16Index))).length
);

/** Convert a domain code-point offset into a JavaScript UTF-16 index. */
export const codePointOffsetToUtf16Index = (value: string, codePointOffset: number): number => (
  Array.from(value).slice(0, Math.max(0, codePointOffset)).join('').length
);

export const sliceByCodePointOffset = (value: string, start: number, end?: number): string => (
  Array.from(value).slice(start, end).join('')
);
